import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { MarkBankTransferPaidDialog } from "./MarkBankTransferPaidDialog";
import { PayoutAvatar, StatCard } from "@/components/admin/payouts/primitives";
import { EmptyState } from "@/components/admin/payouts/EmptyState";
import { SubscriptionsSkeleton } from "./Skeletons";
import {
  fmtDate,
  fmtMoney,
  MoneyValue,
  MobileField,
  PillAction,
  PLAN_LABEL,
  Th,
} from "./subscription-ui";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { throwIfNotOk } from "@/lib/api-error";
import { Bank, Check, CircleNotch, X } from "@phosphor-icons/react";
import type { LedgerListRow } from "@/types";

export function PendingBankTransferTable() {
  const qc = useQueryClient();
  const [markPaidFor, setMarkPaidFor] = useState<LedgerListRow | null>(null);
  const [cancelFor, setCancelFor] = useState<LedgerListRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "subscription-ledger", "pending-bank"],
    queryFn: async () => {
      const res = await fetch(
        "/api/admin/subscription-ledger?status=pending&paymentMethod=bank_transfer",
      );
      await throwIfNotOk(res, "Greška pri učitavanju podataka o pretplatama");
      return res.json() as Promise<{ rows: LedgerListRow[] }>;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await fetch(`/api/admin/subscription-ledger/${id}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      await throwIfNotOk(res, "Greška pri otkazivanju uplatnice");
    },
    onSuccess: () => {
      toast.success("Uplatnica otkazana");
      setCancelFor(null);
      qc.invalidateQueries({ queryKey: ["admin", "subscription-ledger"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const rows = data?.rows ?? [];
  const totalPending = rows.reduce((sum, r) => sum + r.amountGross, 0);

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Uplatnice na čekanju"
          value={String(rows.length)}
          tone="primary"
        />
        <StatCard
          label="Očekivani iznos"
          value={fmtMoney(totalPending)}
          suffix="KM"
        />
      </div>

      {isLoading ? (
        <SubscriptionsSkeleton columns={5} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Bank}
          title="Nema uplatnica na čekanju"
          description="Sve generisane uplatnice su plaćene ili otkazane."
          hasFilters={false}
          onClear={() => {}}
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm lg:block">
            <table className="w-full table-fixed text-[13px]">
              <colgroup>
                <col />
                <col className="w-[130px]" />
                <col className="w-[130px]" />
                <col className="w-[120px]" />
                <col className="w-[230px]" />
              </colgroup>
              <thead>
                <tr className="bg-muted/40">
                  <Th className="pl-4">Terapeut</Th>
                  <Th>Plan</Th>
                  <Th align="right">Iznos</Th>
                  <Th>Generisano</Th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const name = r.therapistName ?? r.therapistFullName;
                  return (
                    <tr
                      key={r.id}
                      className={cn(
                        "transition-colors hover:bg-primary/[0.03]",
                        i !== rows.length - 1 && "border-b border-border/40",
                      )}
                    >
                      <td className="px-3 py-3 pl-4">
                        <div className="flex items-center gap-2.5">
                          <PayoutAvatar
                            name={name}
                            tone="primary"
                            className="size-7 text-[10px]"
                          />
                          <span className="truncate font-medium text-foreground">
                            {name}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-foreground">
                        {PLAN_LABEL[r.planType]}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right">
                        <MoneyValue
                          value={r.amountGross}
                          currency={r.currency}
                          className="font-semibold"
                        />
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 tabular-nums text-foreground">
                        {fmtDate(r.createdAt)}
                      </td>
                      <td className="py-3 pl-1 pr-4 text-right">
                        <div className="inline-flex gap-2">
                          <PillAction
                            icon={Check}
                            onClick={() => setMarkPaidFor(r)}
                          >
                            Označi plaćeno
                          </PillAction>
                          <PillAction
                            icon={X}
                            tone="danger"
                            onClick={() => setCancelFor(r)}
                          >
                            Otkaži
                          </PillAction>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile/Tablet cards */}
          <div className="grid gap-3 sm:grid-cols-2 lg:hidden">
            {rows.map((r) => {
              const name = r.therapistName ?? r.therapistFullName;
              return (
                <div
                  key={r.id}
                  className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <PayoutAvatar
                        name={name}
                        tone="primary"
                        className="size-8 text-[10px]"
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium leading-tight text-foreground">
                          {name}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {PLAN_LABEL[r.planType]}
                        </div>
                      </div>
                    </div>
                    <MoneyValue
                      value={r.amountGross}
                      currency={r.currency}
                      className="shrink-0 text-sm font-semibold"
                    />
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3 border-t border-border/40 pt-3">
                    <MobileField label="Generisano">
                      <span className="tabular-nums">{fmtDate(r.createdAt)}</span>
                    </MobileField>
                    <div className="flex shrink-0 gap-2">
                      <PillAction
                        icon={Check}
                        onClick={() => setMarkPaidFor(r)}
                        className="h-8 text-xs"
                      >
                        Plaćeno
                      </PillAction>
                      <PillAction
                        icon={X}
                        tone="danger"
                        onClick={() => setCancelFor(r)}
                        className="h-8 text-xs"
                      >
                        Otkaži
                      </PillAction>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {markPaidFor && (
        <MarkBankTransferPaidDialog
          ledger={markPaidFor}
          onClose={() => setMarkPaidFor(null)}
        />
      )}

      {cancelFor && (
        <CancelTransferDialog
          ledger={cancelFor}
          isPending={cancelMutation.isPending}
          onConfirm={(reason) =>
            cancelMutation.mutate({ id: cancelFor.id, reason })
          }
          onClose={() => setCancelFor(null)}
        />
      )}
    </div>
  );
}

function CancelTransferDialog({
  ledger,
  isPending,
  onConfirm,
  onClose,
}: {
  ledger: LedgerListRow;
  isPending: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const name = ledger.therapistName ?? ledger.therapistFullName;

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <AdminDialogHeading
          icon={X}
          title="Otkaži uplatnicu"
          description="Unesite razlog kako bi audit trag ostao jasan."
          tone="destructive"
        />
      </DialogHeader>
      <DialogBody>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 rounded-xl bg-muted/40 px-3.5 py-3">
            <PayoutAvatar name={name} tone="primary" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">
                {name}
              </p>
              <p className="text-xs text-muted-foreground">
                {PLAN_LABEL[ledger.planType]} · {fmtMoney(ledger.amountGross)}{" "}
                {ledger.currency}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <label
              htmlFor="cancel-reason"
              className="text-sm font-medium text-foreground"
            >
              Razlog otkazivanja
            </label>
            <Textarea
              id="cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="npr. Terapeut je odustao, generisana nova uplatnica..."
              rows={3}
            />
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          Odustani
        </Button>
        <Button
          variant="destructive"
          onClick={() => onConfirm(reason.trim())}
          disabled={isPending || !reason.trim()}
        >
          {isPending ? (
            <>
              <CircleNotch className="size-4 animate-spin" />
              Otkazivanje...
            </>
          ) : (
            <>
              <X className="size-4" />
              Otkaži uplatnicu
            </>
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
